import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  IconButton
} from "@mui/material";
import * as Yup from "yup";
import { useFormik, FieldArray, FormikProvider } from "formik";
import dayjs from "dayjs";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import { COMPACT_FORM_MODAL_SX } from "../../utils/formModalStyles";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { getLabtestsList } from "../../services/labtestService";
import { getAllStaff } from "../../services/StaffService";
import { createLabTest } from "../../services/PatientlabTestService";
import { getAssignmentByAdmissionId } from "../../services/PatientAssignmentService";
import { isPathModuleStaffRole } from "../../utils/pathRoleUtils";

const validationSchema = Yup.object({
  entries: Yup.array().of(
    Yup.object().shape({
      testId: Yup.string().required("Lab test is required"),
      labId: Yup.string().required("Pathology Admin is required"),
      assignedBy: Yup.string().required("Assigned by is required"),
      testDate: Yup.string().required("Date is required"),
    })
  )
});

const AssignLabTestForm = ({ open, onClose, admissionId, patientId }) => {
  const [labTestList, setLabTestList] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [assignedDoctors, setAssignedDoctors] = useState([]);
  const [doctorName, setDoctorName] = useState(""); // ✅ ADD THIS

  useEffect(() => {
    if (open) {
      fetchLabTestList();
      fetchPathList();
      fetchAssignmentAdmission();
    }
  }, [open]);

  const fetchLabTestList = async () => {
    try {
      const response = await getLabtestsList();
      if (Array.isArray(response)) {
        setLabTestList(response);
      } else {
        setLabTestList([]);
      }
    } catch (error) {
      console.error("Failed to fetch lab tests", error);
      setLabTestList([]);
    }
  };

  const fetchPathList = async () => {
    try {
      const response = await getAllStaff();
      if (Array.isArray(response)) {
        const pathAdmins = response.filter((staff) =>
          isPathModuleStaffRole(staff.userRole)
        );
        setStaffList(pathAdmins);
        if (pathAdmins.length === 0) {
          alert("Failed to assign lab test as no pathology admin is available.");
        }
      } else {
        setStaffList([]);
        alert("Failed to fetch staff data.");
      }
    } catch (error) {
      console.error("Failed to fetch staff", error);
      alert("Error occurred while fetching pathology admins.");
      setStaffList([]);
    }
  };

  const fetchAssignmentAdmission = async () => {
    try {
      const res = await getAssignmentByAdmissionId(admissionId);
      // ✅ Directly access doctorName
      if (res && res.doctorName) {
        setDoctorName(res.doctorName); // ✅ Set doctor name
        setAssignedDoctors([{ id: res.doctorId, name: res.doctorName }]);
      } else {
        setDoctorName("");
        setAssignedDoctors([]);
      }
    } catch (error) {
      console.error("Failed to fetch assignments", error);
      setDoctorName("");
      setAssignedDoctors([]);
    }
  };

  const formik = useFormik({
    initialValues: {
      entries: [
        {
          testId: "",
          labId: "",
          assignedBy: "",
          testDate: dayjs().format("YYYY-MM-DD"),
        }
      ],
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (assignedDoctors.length === 0) {
        alert("Lab test assignment failed. No assigned doctor available.");
        return;
      }
      try {
        const payloads = values.entries.map((item) => ({
          patientId,
          admissionId,
          testId: item.testId,
          labId: item.labId,
          assignedBy: item.assignedBy,
          testDate: dayjs(item.testDate).format("YYYY-MM-DDTHH:mm:ss"),
          testStatus: "PENDING",
          testResult: ""
        }));
        await createLabTest(payloads);

        alert("Path Lab Test Assigned Successfully");
        resetForm();
        onClose();
      } catch (error) {
        alert("Failed to submit lab test data.");
        console.error(error);
      }
    },
  });

  const { values, handleChange, handleSubmit, touched, errors } = formik;

  return (
    <BasicModal open={open} onClose={onClose} sx={COMPACT_FORM_MODAL_SX}>
      <Box>
        <Typography
          variant="h5"
          sx={{ fontSize: "1.5rem", fontWeight: "bold", textAlign: "center", mb: 3 }}
        >
          Assign Lab Tests To Patient
        </Typography>

        <FormikProvider value={formik}>
          <form onSubmit={handleSubmit}>
            <FieldArray name="entries">
              {({ push, remove }) => (
                <Grid container spacing={2}>
                  {/* ✅ Doctor Name Readonly Field */}
                  {/* <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Assigned Doctor"
                      value={doctorName || "Not Assigned"}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Grid> */}

                  {values.entries.map((item, index) => (
                    <React.Fragment key={index}>

                      <Grid item xs={3}>
                        <TextField
                          select
                          fullWidth
                          name={`entries[${index}].testId`}
                          label="Lab Test"
                          value={item.testId}
                          onChange={handleChange}
                          error={
                            touched.entries?.[index]?.testId &&
                            Boolean(errors.entries?.[index]?.testId)
                          }
                          helperText={
                            touched.entries?.[index]?.testId &&
                            errors.entries?.[index]?.testId
                          }
                          SelectProps={{
                            MenuProps: {
                              PaperProps: { style: { maxHeight: 200, overflowY: "auto" } }
                            }
                          }}
                        >
                          {labTestList.map((lab) => (
                            <MenuItem key={lab.id} value={lab.id}>
                              {lab.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={3}>
                        <TextField
                          select
                          fullWidth
                          name={`entries[${index}].assignedBy`}
                          label="Assigned By"
                          value={item.assignedBy}
                          onChange={handleChange}
                          error={
                            touched.entries?.[index]?.assignedBy &&
                            Boolean(errors.entries?.[index]?.assignedBy)
                          }
                          helperText={
                            touched.entries?.[index]?.assignedBy &&
                            errors.entries?.[index]?.assignedBy
                          }
                        >
                          {assignedDoctors.length === 0 ? (
                            <MenuItem disabled value="">
                              No assigned doctor available
                            </MenuItem>
                          ) : (
                            assignedDoctors.map((doc) => (
                              <MenuItem key={doc.id} value={doc.id}>
                                {doc.name}
                              </MenuItem>
                            ))
                          )}
                        </TextField>
                      </Grid>

                      <Grid item xs={3}>
                        <TextField
                          select
                          fullWidth
                          name={`entries[${index}].labId`}
                          label="Assigned To"
                          value={item.labId ?? ""}
                          onChange={(e) =>
                            formik.setFieldValue(
                              `entries[${index}].labId`,
                              parseInt(e.target.value, 10)
                            )
                          }
                          onBlur={formik.handleBlur}
                          error={
                            touched.entries?.[index]?.labId &&
                            Boolean(errors.entries?.[index]?.labId)
                          }
                          helperText={
                            touched.entries?.[index]?.labId &&
                            errors.entries?.[index]?.labId
                          }
                        >
                          {staffList
                            .filter((staff) => isPathModuleStaffRole(staff.userRole))
                            .map((staff) => (
                              <MenuItem key={staff.id} value={staff.userId}>
                                {staff.name
                                  .split(" ")
                                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                  .join(" ")}
                              </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          type="date"
                          name={`entries[${index}].testDate`}
                          label="Test Date"
                          InputLabelProps={{ shrink: true }}
                          value={item.testDate}
                          onChange={handleChange}
                          error={
                            touched.entries?.[index]?.testDate &&
                            Boolean(errors.entries?.[index]?.testDate)
                          }
                          helperText={
                            touched.entries?.[index]?.testDate &&
                            errors.entries?.[index]?.testDate
                          }
                        />
                      </Grid>

                      <Grid item xs={1} display="flex" alignItems="center">
                        {values.entries.length > 1 && (
                          <IconButton onClick={() => remove(index)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Grid>
                    </React.Fragment>
                  ))}

                  <Grid item xs={12} textAlign="right">
                    <IconButton
                      onClick={() =>
                        push({
                          testId: "",
                          assignedBy: "",
                          labId: "",
                          testDate: dayjs().format("YYYY-MM-DD"),
                        })
                      }
                      color="primary"
                    >
                      <AddCircleIcon />
                    </IconButton>
                  </Grid>

                  <Grid item xs={12}>
                    <FormModalActions
                      onCancel={onClose}
                      submitType="submit"
                    />
                  </Grid>
                </Grid>
              )}
            </FieldArray>
          </form>
        </FormikProvider>
      </Box>
    </BasicModal>
  );
};

export default AssignLabTestForm;
